/**
 * Fittext Calculator Utility
 * 
 * Shared logic for calculating optimal font sizes to fit text within containers.
 * Used by both fit-text component and lit-typetester component.
 */

/**
 * Calculate optimal font size to fit text in container
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.text - Text to measure
 * @param {string} options.fontFamily - Font family name
 * @param {string} [options.styleName=''] - Font style name (optional)
 * @param {number} options.containerWidth - Width of container in pixels
 * @param {number} [options.minSize=12] - Minimum font size
 * @param {number} [options.maxSize=300] - Maximum font size
 * @param {number} [options.lineHeight=1.2] - Line height value
 * @param {string} [options.variationSettings='normal'] - CSS font-variation-settings value
 * @param {number} [options.safetyMargin=0.97] - Safety margin factor (0-1)
 * @param {boolean} [options.throwOnFontLoadError=false] - Whether to throw on font load failure
 * @returns {Promise<number>} - Calculated font size in pixels
 */
export async function calculateFitSize({
  text,
  fontFamily,
  styleName = '',
  containerWidth,
  minSize = 12,
  maxSize = 300,
  lineHeight = 1.2,
  variationSettings = 'normal',
  safetyMargin = 0.97,
  throwOnFontLoadError = false
}) {
  // Validate inputs
  if (!fontFamily || !fontFamily.trim()) {
    throw new Error('[FITTEXT] Font family is required')
  }
  
  if (containerWidth <= 0) {
    throw new Error('[FITTEXT] Container width must be positive')
  }

  if (!text || !text.trim()) {
    console.warn('[FITTEXT] Empty text provided, returning minSize')
    return minSize
  }

  // Build combined font string
  const combinedFont = styleName 
    ? `"${fontFamily} ${styleName}"` 
    : `"${fontFamily}"`

  // Wait for font to load
  try {
    await document.fonts.load(`16px ${combinedFont}`)
    console.log('[FITTEXT] Font loaded:', combinedFont)
  } catch (error) {
    console.warn('[FITTEXT] Font load failed:', error)
    if (throwOnFontLoadError) {
      throw error
    }
  }

  // Find longest line if multi-line
  const textToMeasure = findLongestLine(text, combinedFont, variationSettings)

  // Create measurement element
  const tempElement = createMeasurementElement({
    text: textToMeasure,
    fontFamily: combinedFont,
    lineHeight,
    variationSettings
  })

  document.body.appendChild(tempElement)

  // Binary search for optimal size
  let fontSize = maxSize
  while (fontSize >= minSize) {
    tempElement.style.fontSize = `${fontSize}px`
    if (tempElement.offsetWidth <= containerWidth) {
      console.log('[FITTEXT] Calculated size:', fontSize, 'for width:', containerWidth)
      break
    }
    fontSize -= 1
  }

  document.body.removeChild(tempElement)

  // Apply safety margin
  const finalSize = Math.max(Math.floor(fontSize * safetyMargin), minSize)
  console.log('[FITTEXT] Final size with safety margin:', finalSize)
  
  return finalSize
}

/**
 * Find the longest line in multi-line text
 * 
 * @param {string} text - Text that may contain line breaks
 * @param {string} fontFamily - Font family for measurement
 * @param {string} [variationSettings='normal'] - Font variation settings
 * @returns {string} - The longest line
 */
export function findLongestLine(text, fontFamily, variationSettings = 'normal') {
  const hasLineBreaks = text.includes('<br') || text.includes('\n')
  
  if (!hasLineBreaks) {
    return text
  }

  console.log('[FITTEXT] Multi-line text detected, finding longest line')

  const lines = text.split(/<br\s*\/?>|\n/g).map(line => line.trim())
  let longestLine = ''
  let maxLength = 0

  const measureEl = document.createElement('div')
  measureEl.style.position = 'absolute'
  measureEl.style.visibility = 'hidden'
  measureEl.style.whiteSpace = 'nowrap'
  measureEl.style.fontFamily = fontFamily
  measureEl.style.fontSize = '16px'
  measureEl.style.fontVariationSettings = variationSettings
  document.body.appendChild(measureEl)

  for (const line of lines) {
    measureEl.innerHTML = line
    if (measureEl.offsetWidth > maxLength) {
      maxLength = measureEl.offsetWidth
      longestLine = line
    }
  }

  document.body.removeChild(measureEl)
  
  console.log('[FITTEXT] Longest line:', longestLine)
  return longestLine
}

/**
 * Create a measurement element with specified styles
 * 
 * @param {Object} options - Element configuration
 * @param {string} options.text - Text content
 * @param {string} options.fontFamily - Font family
 * @param {number} options.lineHeight - Line height
 * @param {string} options.variationSettings - Font variation settings
 * @returns {HTMLDivElement} - Configured measurement element
 * @private
 */
function createMeasurementElement({ text, fontFamily, lineHeight, variationSettings }) {
  const element = document.createElement('div')
  element.style.position = 'absolute'
  element.style.visibility = 'hidden'
  element.style.whiteSpace = 'nowrap'
  element.style.fontFamily = fontFamily
  element.style.lineHeight = `${lineHeight}`
  element.style.fontVariationSettings = variationSettings
  element.style.boxSizing = 'border-box'
  element.style.padding = '0'
  element.style.margin = '0'
  element.innerHTML = text
  return element
}
